/**
 * Battle Hub — what is actually good right now, and what it runs.
 *
 * TWO METAS, NOT ONE. The page used to show GO Battle League rankings under
 * the bare title "Meta", which is only unambiguous if you already play GBL: a
 * trainer looking for raid counters found league tier lists and no sign that
 * raids were a different question. So the mode switch is the first control on
 * the page, and each mode says in a line what it ranks and who says so.
 *
 * The two halves are deliberately NOT one ranking with a filter. They differ in
 * every way that matters:
 *
 *   PvP    - 1v1 at a CP cap. Ranked by PvPoke, whose judgement we defer to.
 *   Raids  - 6v1 against a boss with fixed typing. Computed here from the
 *            game's own data, because nobody publishes raid rankings the way
 *            PvPoke publishes PvP ones.
 *
 * Merging them would mean inventing a common score, and an invented score is
 * exactly what the hand-written "top 50 meta" lists this page replaced were.
 *
 * A REFERENCE, deliberately, and only for now. It answers "who should I build
 * for Great League", "what does the good Tinkaton run", "which of these are
 * Sinnoh" — without pretending to know which of them the trainer owns. The
 * moment My Pokémon exists this page stops being a reference and starts being a
 * shortlist, because it will be able to mark the ones already in the bag.
 *
 * IT REPLACED THE CUSTOM DEX BUILDER, which let a trainer invent a dex from a
 * filter kind and then filled it from hand-written "top 50 meta" lists that
 * were prototype filler (the Great League list had 50 entries and 25 unique
 * species, Sableye five times). A fixed hub over real, regenerable rankings is
 * a smaller thing to keep correct than a builder over invented ones.
 */
import { useEffect, useState } from "react";
import { getBattleHubMemory, setBattleHubMode, takeBattleHubReturn } from "@/lib/battleHubMemory";
import { getPageScrollTop, scrollPageTo } from "@/lib/pageScroll";
import { Swords, Flame, UserCheck } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { DotRail } from "@/components/DotRail";
import { MenuHeading } from "@/components/MenuHeading";
import { PvpMetaPanel } from "@/components/battle/PvpMetaPanel";
import { RaidMetaPanel } from "@/components/battle/RaidMetaPanel";
import { YoursMetaPanel } from "@/components/battle/YoursMetaPanel";

export type MetaMode = "pvp" | "raid" | "yours";

/**
 * What each mode ranks, and whose judgement it is.
 *
 * The blurb is not decoration: "what meta is this?" was the actual question,
 * and a one-line answer under the switch costs a line and removes the doubt.
 */
export const META_MODES: ReadonlyArray<{
  key: MetaMode;
  label: string;
  blurb: string;
  Icon: typeof Swords;
  color: string;
}> = [
  {
    key: "pvp",
    label: "PvP",
    blurb: "GO Battle League: who wins 1v1 at each CP cap. Ranked by PvPoke.",
    Icon: Swords,
    color: "hsl(207 90% 54%)",
  },
  {
    key: "raid",
    label: "Raids",
    blurb: "Raid attackers: who does the most damage?",
    Icon: Flame,
    color: "hsl(14 90% 57%)",
  },
  {
    key: "yours",
    // The other two modes are references. This one is advice, and the blurb
    // says which half of it your dex can actually answer.
    label: "Yours",
    blurb: "Your Nexus Dex against the rankings: what you have, and what to chase.",
    Icon: UserCheck,
    color: "var(--accent-fill)",
  },
];

export default function BattleHub() {
  // The mode survives a round-trip to a record page (battleHubMemory), so
  // Back from a compare box lands on the meta the trainer left.
  const [mode, setModeState] = useState<MetaMode>(() => getBattleHubMemory().mode);
  const setMode = (next: MetaMode) => {
    setBattleHubMode(next);
    setModeState(next);
  };
  const active = META_MODES.find((m) => m.key === mode) ?? META_MODES[0];

  // Returning from a record opened here: put the page back where it was. The
  // list paints in stages (sprites, the compare boxes), so the offset is
  // re-applied over a short window until it holds, the way the Pokédex
  // region skip settles.
  useEffect(() => {
    const ret = takeBattleHubReturn();
    if (!ret || ret.scrollY <= 0) return;
    const started = Date.now();
    let raf = 0;
    const settle = () => {
      scrollPageTo(ret.scrollY);
      if (Math.abs(getPageScrollTop() - ret.scrollY) > 2 && Date.now() - started < 1200) {
        raf = requestAnimationFrame(settle);
      }
    };
    raf = requestAnimationFrame(settle);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <AppShell pageSpacing>
      <DotRail>
        <div className="pt-12 page-hero-top">
          <MenuHeading
            eyebrow="Battle"
            title="Meta."
            greenSuffix="Right now."
            data-dotrail-skip
          />
        </div>

        {/* The mode switch, ABOVE the filters, because which meta you are
            reading is a bigger question than which type you want from it. */}
        <section className="v2-neon-card-soft p-3 mb-3" data-dotrail-dot data-testid="section-meta-mode">
          <div className="flex gap-1.5" role="tablist" aria-label="Which meta">
            {META_MODES.map((m) => {
              const on = m.key === mode;
              const Icon = m.Icon;
              return (
                <button
                  key={m.key}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setMode(m.key)}
                  data-testid={`meta-mode-${m.key}`}
                  className="flex-1 rounded-full px-2 py-1.5 text-[11px] font-bold inline-flex items-center justify-center gap-1.5"
                  style={{
                    background: on
                      ? `color-mix(in srgb, ${m.color} 22%, transparent)`
                      : "var(--surface-sunken)",
                    color: on ? m.color : "var(--text-tertiary)",
                    border: `1px solid ${on ? m.color : "var(--border-subtle)"}`,
                    minHeight: 34,
                  }}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden />
                  {m.label}
                </button>
              );
            })}
          </div>
          <p
            className="mt-2 text-[10.5px] leading-snug"
            style={{ color: "var(--text-secondary)" }}
            data-testid="meta-mode-blurb"
          >
            {active.blurb}
          </p>
        </section>

        {mode === "pvp" && <PvpMetaPanel />}
        {mode === "raid" && <RaidMetaPanel />}
        {/* A trainer with an empty dex is offered the Raids reference, which
            is useful without one — so "Yours" can hand the mode back. */}
        {mode === "yours" && <YoursMetaPanel onExploreRaids={() => setMode("raid")} />}
      </DotRail>
    </AppShell>
  );
}
